import React, { useEffect, useState } from "react";
import { Link, useNavigate, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ttApi, ttAuth } from "../../lib/ttApi";
import {
  Loader2, LogIn, LogOut, Shield, Beaker, Bug, GitBranch, Rocket,
  AlertTriangle, HeartPulse, LineChart, Layers, ArrowRight, Plus,
  Trash2, CheckCircle2, XCircle, Pause, Camera, PlayCircle, ArrowLeft,
  FileText, Database, GitCompareArrows, Sparkles, LayoutGrid,
} from "lucide-react";
import { ReadinessCenter, CutoverCenter, HypercareCenter, RequirementsCenter } from "./TTCenters";
import { TestDataCenter, SITCenter } from "./TTCenters2";
import { GoLiveCenter, AIAgentsCenter } from "./TTCenters3";
import { TestingAdminLogin, UnifiedView } from "./UnifiedView";

// ─── Login ───────────────────────────────────────────────────────────────
function TTLogin() {
  const nav = useNavigate();
  const [f, setF] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await ttApi.login(f.username.trim(), f.password);
      ttAuth.set(r.token);
      nav("/test-tech/home", { replace: true });
    } catch (e) { setErr(e.message || "Login failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 px-4">
      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-md shadow-sm p-6 w-full max-w-sm" data-testid="tt-login-form">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">Capittance · Test Tech</div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-zinc-900 mt-1">Sign in</h1>
        <p className="text-xs text-zinc-500 mt-1">Credentials are issued by your Capittance super-admin.</p>
        <div className="mt-5 space-y-3">
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Username</div>
            <input type="text" value={f.username} onChange={e => setF({...f, username: e.target.value})}
                   className="fld py-2 px-3 w-full" required data-testid="tt-login-username"/>
          </label>
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Password</div>
            <input type="password" value={f.password} onChange={e => setF({...f, password: e.target.value})}
                   className="fld py-2 px-3 w-full" required data-testid="tt-login-password"/>
          </label>
          {err && <div className="p-2 bg-red-50 border-l-4 border-red-500 text-sm text-red-800" data-testid="tt-login-error">{err}</div>}
          <button type="submit" disabled={busy} className="btn btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="tt-login-submit">
            {busy ? <><Loader2 className="animate-spin" size={14}/> Signing in…</> : <><LogIn size={14}/> Sign in</>}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Protected shell ─────────────────────────────────────────────────────
function TTProtected({ children }) {
  const [state, setState] = useState({ user: null, programs: [], loading: true, err: "" });
  const nav = useNavigate();
  useEffect(() => {
    (async () => {
      if (!ttAuth.get()) { nav("/test-tech", { replace: true }); return; }
      try {
        const [me, progs] = await Promise.all([ttApi.me(), ttApi.programs()]);
        setState({ user: me.user, programs: progs.items || [], loading: false, err: "" });
      } catch (e) {
        ttAuth.clear();
        nav("/test-tech", { replace: true });
      }
    })();
  }, [nav]);
  if (state.loading) return <div className="min-h-screen grid place-items-center text-zinc-500"><Loader2 className="animate-spin"/></div>;
  if (!state.user) return null;
  const ctx = { user: state.user, programs: state.programs };
  return <TTShell ctx={ctx}>{typeof children === "function" ? children(ctx) : children}</TTShell>;
}

function TTShell({ ctx, children }) {
  const nav = useNavigate();
  const signOut = () => { ttAuth.clear(); nav("/test-tech", { replace: true }); };
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 px-6 py-3 flex items-center justify-between">
        <Link to="/test-tech/home" className="flex items-center gap-3" data-testid="tt-nav-home">
          <div className="w-7 h-7 rounded-sm grid place-items-center text-white" style={{ background: "linear-gradient(135deg,#4A0F1A 0%,#7A1F2B 100%)" }}>
            <Shield size={14}/>
          </div>
          <span className="font-display text-base font-extrabold tracking-tight">Test Tech Control Tower</span>
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-xs text-zinc-500">{ctx.user.display_name} · <span className="font-mono">{ctx.user.username}</span></span>
          <button onClick={signOut} className="btn btn-ghost btn-sm inline-flex items-center gap-1" data-testid="tt-signout"><LogOut size={13}/> Sign out</button>
        </div>
      </header>
      {children}
    </div>
  );
}

// ─── Home — 8 command-center tiles ───────────────────────────────────────
const CENTERS = [
  { id: "unified",       label: "Unified View",      icon: LayoutGrid,     status: "live" },
  { id: "program",       label: "Program Control",   icon: Layers,         status: "stub" },
  { id: "requirements",  label: "Requirements · RTM",icon: FileText,       status: "live" },
  { id: "testing",       label: "Testing",           icon: Beaker,         status: "live" },
  { id: "testdata",      label: "Test Data (TDC)",   icon: Database,       status: "live" },
  { id: "sit",           label: "System Integration",icon: GitCompareArrows,status: "live" },
  { id: "defects",       label: "Defects",           icon: Bug,            status: "live" },
  { id: "readiness",     label: "Readiness",         icon: GitBranch,      status: "live" },
  { id: "cutover",       label: "Cutover",           icon: Rocket,         status: "live" },
  { id: "golive",        label: "Go-Live Scorecard", icon: AlertTriangle,  status: "live" },
  { id: "hypercare",     label: "Hypercare",         icon: HeartPulse,     status: "live" },
  { id: "ai",            label: "AI Risk Agents",    icon: Sparkles,       status: "live" },
  { id: "value",         label: "Value Realization", icon: LineChart,      status: "stub" },
];

function TTHome({ ctx }) {
  const [pid, setPid] = useState(ctx.programs[0]?._id || "");
  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-home">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Welcome, {ctx.user.display_name}</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-zinc-900">Enterprise Transformation Control Tower</h1>
          <p className="text-sm text-zinc-600 mt-1">Testing · Cutover · Go-Live · Hypercare · Stabilization · BAU</p>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Active program</div>
          <select value={pid} onChange={e => setPid(e.target.value)} className="fld py-2 px-3" data-testid="tt-program-picker">
            {ctx.programs.length === 0 && <option value="">No programs assigned</option>}
            {ctx.programs.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {ctx.programs.length === 0 ? (
        <div className="p-6 bg-amber-50 border-l-4 border-amber-400 text-amber-900 text-sm rounded-sm">
          Your account hasn't been assigned to a program yet. Ask your super-admin to add you from <code className="font-mono">/admin/test-techs</code>.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="tt-centers">
          {CENTERS.map(c => {
            const Icon = c.icon;
            const live = c.status === "live";
            return (
              <Link
                key={c.id}
                to={live ? `/test-tech/${c.id}/${pid}` : `#`}
                onClick={(e) => { if (!live) e.preventDefault(); }}
                className={`relative p-4 rounded-md border bg-white hover:shadow-md transition-shadow ${live ? "" : "opacity-60 cursor-not-allowed"}`}
                data-testid={`tt-center-${c.id}`}
              >
                <div className="w-9 h-9 rounded-sm grid place-items-center text-white" style={{ background: live ? "linear-gradient(135deg,#4A0F1A 0%,#7A1F2B 100%)" : "#9CA3AF" }}>
                  <Icon size={16}/>
                </div>
                <div className="font-display text-base font-extrabold tracking-tight text-zinc-900 mt-3">{c.label}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                  {live ? "Open ·" : "Coming soon ·"} <span className={live ? "text-emerald-700" : "text-zinc-400"}>{live ? "LIVE" : "WIP"}</span>
                </div>
                {live && <ArrowRight size={13} className="absolute top-4 right-4 text-zinc-400"/>}
              </Link>
            );
          })}
        </div>
      )}
    </main>
  );
}

// ─── Testing command center ──────────────────────────────────────────────
function TestingCenter() {
  const { pid } = useParams();
  const [scenarios, setScenarios] = useState([]);
  const [cases, setCases] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [openSc, setOpenSc] = useState(false);
  const [openCase, setOpenCase] = useState(null); // scenario_id or null
  const [execFor, setExecFor] = useState(null); // case object

  const refresh = async () => {
    const [s, c, k] = await Promise.all([ttApi.listScenarios(pid), ttApi.listCases(pid), ttApi.kpis(pid)]);
    setScenarios(s.items || []); setCases(c.items || []); setKpis(k);
  };
  useEffect(() => { refresh(); }, [pid]);

  const delSc = async (id) => { if (window.confirm("Delete scenario?")) { await ttApi.deleteScenario(pid, id); refresh(); } };
  const delCase = async (id) => { if (window.confirm("Delete test case?")) { await ttApi.deleteCase(pid, id); refresh(); } };

  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-testing">
      <Link to="/test-tech/home" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft size={13}/> Control Tower</Link>
      <div className="flex items-center justify-between mt-2 mb-5">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Testing Command Center</h1>
        <div className="flex gap-2">
          <button onClick={() => setOpenSc(true)} className="btn btn-secondary btn-sm inline-flex items-center gap-1" data-testid="tt-new-scenario"><Plus size={13}/> New scenario</button>
        </div>
      </div>

      {/* KPI tiles */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="tt-testing-kpis">
          <Tile label="Cases"         value={kpis.cases.total}       sub={`${kpis.cases.executed} executed`}/>
          <Tile label="Executed %"    value={`${kpis.cases.executed_pct}%`} sub={`of ${kpis.cases.total}`}/>
          <Tile label="Pass %"        value={`${kpis.cases.pass_pct}%`} sub={`${kpis.cases.passed} passed`} tone={kpis.cases.pass_pct >= 90 ? "green" : kpis.cases.pass_pct >= 70 ? "amber" : "red"}/>
          <Tile label="Failed"        value={kpis.cases.failed}      sub={`${kpis.cases.blocked} blocked`} tone={kpis.cases.failed > 0 ? "red" : "green"}/>
        </div>
      )}

      {/* Scenarios → cases */}
      <div className="space-y-4">
        {scenarios.length === 0 && (
          <div className="p-6 bg-white border border-dashed border-zinc-300 rounded-sm text-center text-sm text-zinc-500">
            No test scenarios yet. Click <b>New scenario</b> to begin — try Order-to-Cash, Procure-to-Pay, Record-to-Report.
          </div>
        )}
        {scenarios.map(sc => {
          const cs = cases.filter(c => c.scenario_id === sc._id);
          return (
            <div key={sc._id} className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid={`tt-scenario-${sc._id}`}>
              <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{sc.process || "Scenario"} · {sc.type}</div>
                  <div className="font-display text-base font-extrabold tracking-tight">{sc.name}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setOpenCase(sc._id)} className="btn btn-ghost btn-sm inline-flex items-center gap-1" data-testid={`tt-add-case-${sc._id}`}><Plus size={13}/> Case</button>
                  <button onClick={() => delSc(sc._id)} className="text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1"><Trash2 size={12}/></button>
                </div>
              </div>
              {cs.length === 0 ? (
                <div className="p-4 text-sm text-zinc-500">No cases under this scenario yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-white text-xs font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                    <tr><th className="text-left px-4 py-2">Case</th><th className="text-left px-4 py-2">Priority</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Last run</th><th className="text-right px-4 py-2">Actions</th></tr>
                  </thead>
                  <tbody>
                    {cs.map(c => (
                      <tr key={c._id} className="border-b border-zinc-50 last:border-0" data-testid={`tt-case-${c._id}`}>
                        <td className="px-4 py-2">{c.title}</td>
                        <td className="px-4 py-2 text-xs">{c.priority}</td>
                        <td className="px-4 py-2"><StatusPill status={c.status}/></td>
                        <td className="px-4 py-2 text-xs text-zinc-500 font-mono">{c.last_run_at ? new Date(c.last_run_at).toLocaleString() : "—"}</td>
                        <td className="px-4 py-2 text-right">
                          <button onClick={() => setExecFor(c)} className="text-xs text-emerald-700 hover:text-emerald-900 underline underline-offset-2 inline-flex items-center gap-1" data-testid={`tt-exec-${c._id}`}><PlayCircle size={12}/> Execute</button>
                          <button onClick={() => delCase(c._id)} className="ml-3 text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1"><Trash2 size={11}/></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      {openSc && <ScenarioModal onClose={() => setOpenSc(false)} onCreated={() => { setOpenSc(false); refresh(); }} pid={pid}/>}
      {openCase && <CaseModal scenarioId={openCase} onClose={() => setOpenCase(null)} onCreated={() => { setOpenCase(null); refresh(); }} pid={pid}/>}
      {execFor && <ExecuteModal c={execFor} onClose={() => setExecFor(null)} onDone={() => { setExecFor(null); refresh(); }} pid={pid}/>}
    </main>
  );
}

function Tile({ label, value, sub, tone }) {
  const toneClass = { green: "text-emerald-700", amber: "text-amber-700", red: "text-red-700" }[tone] || "text-zinc-900";
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-3xl font-extrabold tracking-tight mt-1 ${toneClass}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    "Pass": ["pill-green", CheckCircle2],
    "Fail": ["bg-red-50 text-red-700 border border-red-200", XCircle],
    "Blocked": ["bg-amber-50 text-amber-800 border border-amber-200", Pause],
    "Not Run": ["pill-neutral", Pause],
  };
  const [cls, Icon] = map[status] || map["Not Run"];
  return <span className={`pill ${cls} inline-flex items-center gap-1 text-[10px]`}><Icon size={11}/> {status}</span>;
}

function ScenarioModal({ pid, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", process: "Order-to-Cash", type: "happy", description: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await ttApi.createScenario(pid, f); onCreated(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title="New Scenario" onClose={onClose} testid="tt-scenario-modal">
      <form onSubmit={submit} className="space-y-3">
        <F label="Name" v={f.name} onChange={v => setF({...f, name: v})} testid="tt-scenario-name"/>
        <div className="grid grid-cols-2 gap-3">
          <F label="Process" v={f.process} onChange={v => setF({...f, process: v})} testid="tt-scenario-process"/>
          <L label="Type">
            <select value={f.type} onChange={e => setF({...f, type: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-scenario-type">
              {["happy","negative","exception","volume","intercompany","returns","other"].map(x => <option key={x}>{x}</option>)}
            </select>
          </L>
        </div>
        <F label="Description" v={f.description} onChange={v => setF({...f, description: v})} testid="tt-scenario-desc"/>
        {err && <Err>{err}</Err>}
        <ModalActions onClose={onClose} busy={busy} testid="tt-scenario-submit"/>
      </form>
    </SmallModal>
  );
}

function CaseModal({ scenarioId, pid, onClose, onCreated }) {
  const [f, setF] = useState({ title: "", priority: "Medium", tester: "", preconditions: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await ttApi.createCase(pid, { scenario_id: scenarioId, ...f }); onCreated(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title="New Test Case" onClose={onClose} testid="tt-case-modal">
      <form onSubmit={submit} className="space-y-3">
        <F label="Title" v={f.title} onChange={v => setF({...f, title: v})} testid="tt-case-title"/>
        <div className="grid grid-cols-2 gap-3">
          <L label="Priority">
            <select value={f.priority} onChange={e => setF({...f, priority: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-case-priority">
              {["Low","Medium","High","Critical"].map(x => <option key={x}>{x}</option>)}
            </select>
          </L>
          <F label="Tester" v={f.tester} onChange={v => setF({...f, tester: v})} testid="tt-case-tester"/>
        </div>
        <F label="Preconditions" v={f.preconditions} onChange={v => setF({...f, preconditions: v})} testid="tt-case-pre"/>
        {err && <Err>{err}</Err>}
        <ModalActions onClose={onClose} busy={busy} testid="tt-case-submit"/>
      </form>
    </SmallModal>
  );
}

function ExecuteModal({ c, pid, onClose, onDone }) {
  const [f, setF] = useState({ status: "Pass", actual_result: "", notes: "" });
  const [evidence, setEvidence] = useState(null); // { name, b64 }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const onFile = (file) => {
    if (!file) return setEvidence(null);
    if (file.size > 1_000_000) { setErr("Evidence must be under 1 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setEvidence({ name: file.name, b64: reader.result.toString().split(",")[1] || "" });
    reader.readAsDataURL(file);
  };
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await ttApi.executeCase(pid, c._id, {
        ...f,
        evidence_b64: evidence?.b64 || undefined,
        evidence_name: evidence?.name || undefined,
      });
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={`Execute: ${c.title}`} onClose={onClose} testid="tt-execute-modal">
      <form onSubmit={submit} className="space-y-3">
        <L label="Status">
          <select value={f.status} onChange={e => setF({...f, status: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-exec-status">
            {["Pass","Fail","Blocked","Not Run"].map(x => <option key={x}>{x}</option>)}
          </select>
        </L>
        <F label="Actual result" v={f.actual_result} onChange={v => setF({...f, actual_result: v})} testid="tt-exec-actual"/>
        <F label="Notes" v={f.notes} onChange={v => setF({...f, notes: v})} testid="tt-exec-notes"/>
        <L label="Evidence (screenshot or doc, max 1 MB)">
          <input type="file" onChange={e => onFile(e.target.files?.[0])} className="text-xs" data-testid="tt-exec-evidence"/>
          {evidence && <div className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1"><Camera size={11}/> {evidence.name}</div>}
        </L>
        {err && <Err>{err}</Err>}
        <ModalActions onClose={onClose} busy={busy} testid="tt-exec-submit" label="Record"/>
      </form>
    </SmallModal>
  );
}

// ─── Defects command center ──────────────────────────────────────────────
const DEF_STATUSES = ["New","Triaged","Assigned","In Progress","Fixed","Retest","Closed","Rejected","Deferred"];
const DEF_SEVERITIES = ["Critical","High","Medium","Low"];

function DefectsCenter() {
  const { pid } = useParams();
  const [defects, setDefects] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [editDef, setEditDef] = useState(null);
  const [filter, setFilter] = useState({ status: "", severity: "" });

  const refresh = async () => {
    const [d, k] = await Promise.all([ttApi.listDefects(pid), ttApi.kpis(pid)]);
    setDefects(d.items || []); setKpis(k);
  };
  useEffect(() => { refresh(); }, [pid]);

  const filtered = defects.filter(d =>
    (!filter.status || d.status === filter.status) &&
    (!filter.severity || d.severity === filter.severity)
  );

  const del = async (id) => { if (window.confirm("Delete defect?")) { await ttApi.deleteDefect(pid, id); refresh(); } };

  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-defects">
      <Link to="/test-tech/home" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft size={13}/> Control Tower</Link>
      <div className="flex items-center justify-between mt-2 mb-5">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Defects Command Center</h1>
        <button onClick={() => setOpenNew(true)} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="tt-new-defect"><Plus size={13}/> New defect</button>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="tt-defect-kpis">
          <Tile label="All"       value={kpis.defects.total} sub={`${kpis.defects.open} open`}/>
          <Tile label="Critical"  value={kpis.defects.by_severity.Critical} sub="severity" tone={kpis.defects.by_severity.Critical > 0 ? "red" : "green"}/>
          <Tile label="High"      value={kpis.defects.by_severity.High}     sub="severity" tone={kpis.defects.by_severity.High > 0 ? "amber" : "green"}/>
          <Tile label="Medium"    value={kpis.defects.by_severity.Medium}   sub="severity"/>
          <Tile label="Low"       value={kpis.defects.by_severity.Low}      sub="severity"/>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <select value={filter.status} onChange={e => setFilter({...filter, status: e.target.value})} className="fld py-1 px-2 text-xs" data-testid="tt-defect-filter-status">
          <option value="">All statuses</option>
          {DEF_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filter.severity} onChange={e => setFilter({...filter, severity: e.target.value})} className="fld py-1 px-2 text-xs" data-testid="tt-defect-filter-severity">
          <option value="">All severities</option>
          {DEF_SEVERITIES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-widest text-zinc-500">
            <tr><th className="text-left px-4 py-2">ID</th><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">Severity</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Owner</th><th className="text-left px-4 py-2">Created</th><th className="text-right px-4 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500 text-sm">No defects logged yet.</td></tr>
            )}
            {filtered.map(d => (
              <tr key={d._id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`tt-defect-${d._id}`}>
                <td className="px-4 py-2 font-mono text-xs">{d._id.slice(-6)}</td>
                <td className="px-4 py-2">{d.title}</td>
                <td className="px-4 py-2"><SevPill s={d.severity}/></td>
                <td className="px-4 py-2 text-xs"><span className="pill pill-neutral text-[10px]">{d.status}</span></td>
                <td className="px-4 py-2 text-xs text-zinc-500">{d.owner || "—"}</td>
                <td className="px-4 py-2 text-xs text-zinc-500 font-mono">{new Date(d.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setEditDef(d)} className="text-xs text-zinc-700 hover:text-zinc-900 underline underline-offset-2" data-testid={`tt-defect-edit-${d._id}`}>Edit</button>
                  <button onClick={() => del(d._id)} className="ml-3 text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1"><Trash2 size={11}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openNew && <DefectModal pid={pid} onClose={() => setOpenNew(false)} onDone={() => { setOpenNew(false); refresh(); }}/>}
      {editDef && <DefectModal pid={pid} initial={editDef} onClose={() => setEditDef(null)} onDone={() => { setEditDef(null); refresh(); }}/>}
    </main>
  );
}

function SevPill({ s }) {
  const map = {
    Critical: "bg-red-50 text-red-700 border border-red-200",
    High: "bg-amber-50 text-amber-800 border border-amber-200",
    Medium: "pill-neutral",
    Low: "pill-neutral",
  };
  return <span className={`pill text-[10px] ${map[s] || "pill-neutral"}`}>{s}</span>;
}

function DefectModal({ pid, initial, onClose, onDone }) {
  const [f, setF] = useState({
    title: initial?.title || "",
    severity: initial?.severity || "Medium",
    status: initial?.status || "New",
    owner: initial?.owner || "",
    description: initial?.description || "",
    linked_case_id: initial?.linked_case_id || "",
    root_cause: initial?.root_cause || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (initial) await ttApi.updateDefect(pid, initial._id, f);
      else await ttApi.createDefect(pid, f);
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={initial ? "Edit Defect" : "New Defect"} onClose={onClose} testid="tt-defect-modal">
      <form onSubmit={submit} className="space-y-3">
        <F label="Title" v={f.title} onChange={v => setF({...f, title: v})} testid="tt-defect-title"/>
        <div className="grid grid-cols-2 gap-3">
          <L label="Severity">
            <select value={f.severity} onChange={e => setF({...f, severity: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-defect-severity">
              {DEF_SEVERITIES.map(x => <option key={x}>{x}</option>)}
            </select>
          </L>
          <L label="Status">
            <select value={f.status} onChange={e => setF({...f, status: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-defect-status">
              {DEF_STATUSES.map(x => <option key={x}>{x}</option>)}
            </select>
          </L>
        </div>
        <F label="Owner" v={f.owner} onChange={v => setF({...f, owner: v})} testid="tt-defect-owner"/>
        <F label="Linked case ID (optional)" v={f.linked_case_id} onChange={v => setF({...f, linked_case_id: v})} testid="tt-defect-linked"/>
        <F label="Description" v={f.description} onChange={v => setF({...f, description: v})} testid="tt-defect-desc"/>
        <F label="Root cause (if known)" v={f.root_cause} onChange={v => setF({...f, root_cause: v})} testid="tt-defect-rc"/>
        {err && <Err>{err}</Err>}
        <ModalActions onClose={onClose} busy={busy} testid="tt-defect-submit"/>
      </form>
    </SmallModal>
  );
}

// ─── UI helpers ──────────────────────────────────────────────────────────
function SmallModal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center px-4" style={{ background: "rgba(15,23,42,0.55)" }} onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-md shadow-2xl max-w-md w-full p-5 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="font-display text-lg font-extrabold tracking-tight mb-3">{title}</div>
        {children}
      </div>
    </div>
  );
}
const L = ({ label, children }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    {children}
  </label>
);
const F = ({ label, v, onChange, testid, type = "text" }) => (
  <L label={label}>
    <input type={type} value={v} onChange={e => onChange(e.target.value)} className="fld py-2 px-3 w-full" data-testid={testid}/>
  </L>
);
const Err = ({ children }) => <div className="p-2 bg-red-50 border-l-4 border-red-500 text-sm text-red-800">{children}</div>;
const ModalActions = ({ onClose, busy, testid, label = "Save" }) => (
  <div className="flex items-center justify-end gap-2 pt-2">
    <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
    <button type="submit" disabled={busy} className="btn btn-primary btn-sm" data-testid={testid}>{busy ? "Saving…" : label}</button>
  </div>
);

// ─── Default export: Routes ──────────────────────────────────────────────
export default function TestTechApp() {
  return (
    <Routes>
      <Route path="" element={<TTLogin />} />
      <Route path="home" element={<TTProtected>{(ctx) => <TTHome ctx={ctx}/>}</TTProtected>} />
      <Route path="testing/:pid" element={<TTProtected><TestingCenter/></TTProtected>} />
      <Route path="defects/:pid" element={<TTProtected><DefectsCenter/></TTProtected>} />
      <Route path="requirements/:pid" element={<TTProtected><RequirementsCenter/></TTProtected>} />
      <Route path="readiness/:pid" element={<TTProtected><ReadinessCenter/></TTProtected>} />
      <Route path="cutover/:pid" element={<TTProtected><CutoverCenter/></TTProtected>} />
      <Route path="hypercare/:pid" element={<TTProtected><HypercareCenter/></TTProtected>} />
      <Route path="testdata/:pid" element={<TTProtected><TestDataCenter/></TTProtected>} />
      <Route path="sit/:pid" element={<TTProtected><SITCenter/></TTProtected>} />
      <Route path="golive/:pid" element={<TTProtected><GoLiveCenter/></TTProtected>} />
      <Route path="ai/:pid" element={<TTProtected><AIAgentsCenter/></TTProtected>} />
      <Route path="unified/:pid" element={<TTProtected><UnifiedView/></TTProtected>} />
      <Route path="*" element={<Navigate to="/test-tech" replace/>} />
    </Routes>
  );
}
